import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { RefreshCw, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sql-explorer/slow-queries`;

const when = (ts) => new Date(ts * 1000).toLocaleTimeString();
const msTone = (ms) => (ms >= 3000 ? "text-red-600" : ms >= 1000 ? "text-amber-600" : "text-zinc-700");

function Row({ e, i }) {
  const [full, setFull] = useState(false);
  return (
    <div className="border-b border-zinc-100 py-2 text-xs" data-testid={`slow-query-row-${i}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`font-mono font-bold w-16 ${msTone(e.ms)}`} data-testid={`slow-query-ms-${i}`}>{e.ms} ms</span>
        <Badge variant="outline" className="font-mono text-[10px]" data-testid={`slow-query-page-${i}`}>{e.page || "(unknown page)"}</Badge>
        <span className="text-zinc-500 font-mono truncate max-w-[260px]" title={e.endpoint}>{e.endpoint}</span>
        <span className="ml-auto text-zinc-400">{when(e.at)}{e.rows != null ? ` · ${e.rows} rows` : ""}</span>
      </div>
      {e.error && <p className="text-red-600 mt-1">{e.error}</p>}
      <pre
        onClick={() => setFull((v) => !v)}
        className={`mt-1 font-mono text-[11px] text-zinc-600 bg-zinc-50 rounded p-1.5 cursor-pointer whitespace-pre-wrap break-all ${full ? "" : "line-clamp-2"}`}
        title="Click to expand / collapse"
      >{e.sql}</pre>
    </div>
  );
}

export default function SlowQueryDialog({ open, onOpenChange, isAdmin }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("slowest");

  const load = useCallback(async () => {
    setBusy(true);
    try { setData((await axios.get(API)).data); } catch { /* keep previous */ } finally { setBusy(false); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const clear = async () => { await axios.delete(API); load(); };
  const list = data ? (tab === "slowest" ? data.slowest : data.recent) : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="slow-query-dialog">
        <DialogHeader>
          <DialogTitle>Slow ERP queries</DialogTitle>
          <DialogDescription>
            Since last backend restart: {data?.totals?.count ?? 0} queries · {Math.round((data?.totals?.ms ?? 0) / 1000)} s total. Click a query to expand it.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          {["slowest", "recent"].map((t) => (
            <Button key={t} size="sm" variant={tab === t ? "default" : "outline"} className="h-7 text-xs capitalize" onClick={() => setTab(t)} data-testid={`slow-query-tab-${t}`}>
              {t === "slowest" ? "Top 20 slowest" : "Recent"}
            </Button>
          ))}
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={load} disabled={busy} data-testid="slow-query-refresh-btn">
              <RefreshCw className={`w-3 h-3 mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh
            </Button>
            {isAdmin && (
              <Button size="sm" variant="outline" className="h-7 text-xs text-red-600" onClick={clear} data-testid="slow-query-clear-btn">
                <Trash2 className="w-3 h-3 mr-1" /> Clear
              </Button>
            )}
          </div>
        </div>
        {data?.by_page?.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="slow-query-by-page">
            {data.by_page.slice(0, 8).map((p) => (
              <span key={p.page} className="text-[10px] font-mono rounded-full border border-zinc-200 px-2 py-0.5 text-zinc-600">
                {p.page} · {p.count}q · {(p.ms / 1000).toFixed(1)}s · max {p.max_ms}ms
              </span>
            ))}
          </div>
        )}
        <div className="overflow-y-auto flex-1 min-h-0 pr-1">
          {list.length === 0 ? (
            <p className="text-sm text-zinc-500 py-6 text-center" data-testid="slow-query-empty">No ERP queries recorded yet.</p>
          ) : list.map((e, i) => <Row key={`${e.at}-${i}`} e={e} i={i} />)}
        </div>
      </DialogContent>
    </Dialog>
  );
}
